import { useAuth, useSignIn } from '@clerk/clerk-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { FormInput } from '@/components/common/form-inputs/FormInput';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { toast } from '@/hooks/ui/use-toast';

const forgetPasswordFormSchema = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
});

const ForgetPasswordForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const { isLoaded, signIn } = useSignIn();
  const { isSignedIn } = useAuth();

  const forgetForm = useForm<z.infer<typeof forgetPasswordFormSchema>>({
    resolver: zodResolver(forgetPasswordFormSchema),
    defaultValues: {
      email: '',
    },
  });

  if (!isLoaded)
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );

  if (isSignedIn) {
    navigate('/dashboard');
  }

  const forgetFormSubmit = async (
    values: z.infer<typeof forgetPasswordFormSchema>
  ) => {
    setLoading(true);
    await signIn
      ?.create({
        strategy: 'reset_password_email_code',
        identifier: values.email,
      })
      .then(() => {
        toast({
          title: 'Sent reset code to your mail. Please check your mail',
        });
        navigate(
          `/${window.localStorage.getItem('active-org-id')}/auth/reset-password`
        );
      })
      .catch((err) => {
        console.error('error', err.errors[0].longMessage);
        toast({
          variant: 'destructive',
          title: 'Error',
          description: `${err.errors[0].longMessage}`,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Form {...forgetForm}>
      <form
        onSubmit={forgetForm.handleSubmit(forgetFormSubmit)}
        className="w-full space-y-4"
      >
        <div className="relative space-y-4">
          <FormInput
            control={forgetForm.control}
            name="email"
            type="email"
            placeholder="Please enter your mail"
          />
        </div>
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? <Loader2 className="size-4 animate-spin" /> : 'Reset'}
        </Button>
      </form>
    </Form>
  );
};

export { ForgetPasswordForm };
